import { Alert, AlertDescription, AlertTitle } from '@tempo-io/sui-alert';
import { Card, CardContent } from '@tempo-io/sui-card';
import { Progress } from '@tempo-io/sui-progress';
import { Select } from '@tempo-io/sui-select';
import { Separator } from '@tempo-io/sui-separator';
import { Skeleton } from '@tempo-io/sui-skeleton';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Item } from '@/common/api/types/items';
import type { Team } from '@/common/api/types/teams';
import { DatePickerField } from '@/common/components/date-picker-field/DatePickerField';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import {
  formatLocalDate,
  toISODateString,
  toISOTimestamp,
  toLocalMidnight,
} from '@/common/utils/dateFormats';
import { HealthBadgeWithTooltip } from '@/features/insights-dashboard/components/shared/HealthBadgeWithTooltip';
import { ImputedBadge } from '@/features/items/components/data-table/cells/ImputedBadge';
import { GroupedEstimationSelect } from '@/features/items/components/estimation/GroupedEstimationSelect';
import { findSelectedGroupedOption } from '@/features/items/components/estimation/useGroupedEstimationOptions';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { getIsDeadlineAtRisk } from '@/features/planner/components/common/lib/getIsAtRisk';
import { TooltipDatesSection } from '@/features/planner/components/common/TooltipDatesSection';
import { buildGroupedTeamOptions } from '@/features/teams/utils/buildGroupedTeamOptions';
import type { PlanSchedulingSchema, PlanSchedulingTabAdapter } from '../adapters/types';
import { useTabAdapter } from '../context/TabAdapterContext';
import { useGroupedEstimationOptionsWithPublished } from '../hooks/useGroupedEstimationOptionsWithPublished';
import { useItemPanelQueryWithPublished } from '../hooks/useItemPanelQueryWithPublished';
import { useItemProgressWithPublished } from '../hooks/useItemProgressWithPublished';
import { useScheduleBarData } from '../hooks/useScheduleBarData';

type FieldName = keyof PlanSchedulingSchema;
type DateFieldName = Extract<
  FieldName,
  'earliestStartDateTime' | 'deadlineDateTime' | 'plannedStartDateTime' | 'plannedFinishDateTime'
>;

type FieldState = {
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
};

const defaultFieldState: FieldState = {
  isPending: false,
  isSuccess: false,
  isError: false,
  error: null,
};

const OptionWithIcon = ({
  option,
  showImputedBadge,
}: {
  option: { label: string; icon?: React.ReactNode | null };
  showImputedBadge?: boolean;
}) => (
  <div className="flex items-center gap-2">
    {showImputedBadge && <ImputedBadge />}
    {option.icon}
    <span>{option.label}</span>
  </div>
);

const mapTeamToOption = (team: Team) => ({
  value: team.id,
  label: team.name,
  icon: team.icon ? <img src={team.icon} alt={team.name} className="w-4 h-4 rounded-full" /> : null,
});

interface PlanSchedulingProps {
  item: Item;
}

export const PlanScheduling = ({ item }: PlanSchedulingProps) => {
  const { t } = useTranslation();
  const adapter = useTabAdapter<PlanSchedulingTabAdapter>();
  const plannerModuleEnabled = useFlag('plannerModuleEnabled');
  const teamId = adapter.getFieldValue('teamId');
  const earliestStartDateTime = adapter.getFieldValue('earliestStartDateTime');
  const deadlineDateTime = adapter.getFieldValue('deadlineDateTime');
  const plannedStartDateTime = adapter.getFieldValue('plannedStartDateTime');
  const plannedFinishDateTime = adapter.getFieldValue('plannedFinishDateTime');
  const estimationId = adapter.getFieldValue('estimationId');

  const [localDates, setLocalDates] = useState<
    Record<DateFieldName, PlanSchedulingSchema[DateFieldName]>
  >({
    earliestStartDateTime,
    deadlineDateTime,
    plannedStartDateTime,
    plannedFinishDateTime,
  });

  useEffect(() => {
    setLocalDates((prev) => ({ ...prev, earliestStartDateTime }));
  }, [earliestStartDateTime]);

  useEffect(() => {
    setLocalDates((prev) => ({ ...prev, deadlineDateTime }));
  }, [deadlineDateTime]);

  useEffect(() => {
    setLocalDates((prev) => ({ ...prev, plannedStartDateTime }));
  }, [plannedStartDateTime]);

  useEffect(() => {
    setLocalDates((prev) => ({
      ...prev,
      plannedFinishDateTime,
    }));
  }, [plannedFinishDateTime]);

  const { teams, estimations, estimationScheme } = useItemPanelQueryWithPublished(item);
  const {
    groupedOptions,
    flatOptions,
    isLoading: isGroupedEstimationLoading,
  } = useGroupedEstimationOptionsWithPublished({ enabled: true });

  const itemIds = useMemo(() => [item.id], [item.id]);
  const {
    progressPercentage,
    healthLevel,
    healthRiskTypes,
    isLoading: isHealthLoading,
  } = useItemProgressWithPublished({
    itemId: item.id,
    itemIds,
  });
  const {
    forecastedStart,
    forecastedFinish,
    latestFinish,
    isLoading: isScheduleLoading,
  } = useScheduleBarData(item.id, adapter.scenarioContext, plannerModuleEnabled);

  const [fieldStates, setFieldStates] = useState<Record<FieldName, FieldState>>({
    teamId: defaultFieldState,
    earliestStartDateTime: defaultFieldState,
    deadlineDateTime: defaultFieldState,
    plannedStartDateTime: defaultFieldState,
    plannedFinishDateTime: defaultFieldState,
    estimationId: defaultFieldState,
  });

  const setFieldState = (field: FieldName, partial: Partial<FieldState>) => {
    setFieldStates((prev) => ({
      ...prev,
      [field]: { ...prev[field], ...partial },
    }));
  };

  const clearFieldSuccess = (field: FieldName) => {
    setFieldStates((prev) => ({
      ...prev,
      [field]: { ...prev[field], isSuccess: false },
    }));
  };

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    (Object.entries(fieldStates) as [FieldName, FieldState][]).forEach(([field, state]) => {
      if (state.isSuccess) {
        const timer = setTimeout(() => clearFieldSuccess(field), 2000);
        timers.push(timer);
      }
    });

    return () => timers.forEach(clearTimeout);
    // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  }, [fieldStates, clearFieldSuccess]);

  const teamAssignmentOptions = useMemo(
    () =>
      buildGroupedTeamOptions(teams, item.loopId, mapTeamToOption, {
        inPortfolio: t(($) => $.adaptivePlanner.teamDropdown.group.inPortfolio),
        notInPortfolio: t(($) => $.adaptivePlanner.teamDropdown.group.notInPortfolio),
      }),
    [teams, item.loopId, t],
  );

  const selectedTeam = teams.find((team) => team.id === teamId);
  const selectedTeamAssignmentOption = selectedTeam ? mapTeamToOption(selectedTeam) : undefined;
  const selectedGroupedEstimationOption = findSelectedGroupedOption({
    estimationId,
    flatOptions,
    estimations,
  });

  const handleTeamAssignmentChange = async (value: number | undefined) => {
    try {
      setFieldState('teamId', {
        isPending: true,
        isError: false,
        error: null,
        isSuccess: false,
      });
      await adapter.updateFieldValue('teamId', value ?? null);
      setFieldState('teamId', { isPending: false, isSuccess: true });
    } catch (err) {
      setFieldState('teamId', {
        isPending: false,
        isError: true,
        error: err instanceof Error ? err : new Error('Failed to update team assignment'),
      });
    }
  };

  const createDateChangeHandler =
    (field: DateFieldName) => async (value: string | null | undefined) => {
      const isoTimestamp = toISOTimestamp(value);
      const previousValue = localDates[field];
      setLocalDates((prev) => ({ ...prev, [field]: isoTimestamp }));
      try {
        setFieldState(field, {
          isPending: true,
          isError: false,
          error: null,
          isSuccess: false,
        });
        await adapter.updateFieldValue(field, isoTimestamp);
        setFieldState(field, { isPending: false, isSuccess: true });
      } catch (err) {
        setLocalDates((prev) => ({ ...prev, [field]: previousValue }));
        setFieldState(field, {
          isPending: false,
          isError: true,
          error: err instanceof Error ? err : new Error(`Failed to update ${field}`),
        });
      }
    };

  const handleEarliestStartDateChange = createDateChangeHandler('earliestStartDateTime');
  const handleDeadlineDateChange = createDateChangeHandler('deadlineDateTime');
  const handlePlannedStartDateChange = createDateChangeHandler('plannedStartDateTime');
  const handlePlannedFinishDateChange = createDateChangeHandler('plannedFinishDateTime');

  const finishDateMinDate = localDates.earliestStartDateTime
    ? toLocalMidnight(localDates.earliestStartDateTime)
    : undefined;
  const startDateMaxDate = localDates.deadlineDateTime
    ? toLocalMidnight(localDates.deadlineDateTime)
    : undefined;

  const plannedFinishDateMinDate = localDates.plannedStartDateTime
    ? toLocalMidnight(localDates.plannedStartDateTime)
    : undefined;
  const plannedStartDateMaxDate = localDates.plannedFinishDateTime
    ? toLocalMidnight(localDates.plannedFinishDateTime)
    : undefined;

  const hasScheduleDates = plannerModuleEnabled && !!forecastedStart && !!forecastedFinish;
  const isEarliestStartVisible = plannerModuleEnabled && adapter.isVisible('earliestStartDateTime');
  const isItemAtRisk = adapter.scenarioContext
    ? getIsDeadlineAtRisk({ forecastedFinish, deadline: localDates.deadlineDateTime })
    : false;

  const effortSchemeName = useMemo(() => {
    const schemeId = selectedGroupedEstimationOption?.schemeId;
    if (schemeId == null) {
      return estimationScheme?.name ?? '';
    }
    return (
      groupedOptions.find((g) => g.options[0]?.schemeId === schemeId)?.label ??
      estimationScheme?.name ??
      ''
    );
  }, [groupedOptions, selectedGroupedEstimationOption, estimationScheme]);

  const handleEstimationChange = async (value: number | undefined) => {
    try {
      setFieldState('estimationId', {
        isPending: true,
        isError: false,
        error: null,
        isSuccess: false,
      });
      await adapter.updateFieldValue('estimationId', value ?? null);
      setFieldState('estimationId', { isPending: false, isSuccess: true });
    } catch (err) {
      setFieldState('estimationId', {
        isPending: false,
        isError: true,
        error: err instanceof Error ? err : new Error('Failed to update effort estimate'),
      });
    }
  };

  return (
    <div className="flex cursor-default flex-col gap-4">
      <div className="flex flex-row items-center gap-1 text-lg font-bold text-text-neutral-secondary">
        {t(($) => $.adaptivePlanner.itemPanel.planScheduling.label)}
      </div>
      <div className="flex flex-col gap-4">
        {adapter.isVisible('teamId') && (
          <FieldWithStatusIndicator
            inputId="plan-scheduling-team"
            name={t(($) => $.createItem.fields.team.label)}
            isPending={fieldStates.teamId.isPending}
            isSuccess={fieldStates.teamId.isSuccess}
          >
            <div className="flex flex-col gap-1">
              <Select
                key={`plan-team-${teamId}-${item.id}`}
                id="plan-scheduling-team"
                name="planSchedulingTeam"
                isClearable
                value={selectedTeamAssignmentOption}
                onChange={(opt) => handleTeamAssignmentChange(opt?.value)}
                options={teamAssignmentOptions}
                formatOptionLabel={(option, { context }) => (
                  <OptionWithIcon
                    option={option}
                    showImputedBadge={adapter.isImputed?.('teamId') && context === 'value'}
                  />
                )}
                getOptionValue={(option) => String(option.value)}
                getOptionLabel={(option) => option.label}
                placeholder={t(($) => $.common.select)}
                isDisabled={!adapter.isEditable('teamId') || adapter.isDisabled('teamId')}
                menuPosition="fixed"
              />
              {fieldStates.teamId.isError && fieldStates.teamId.error && (
                <span className="text-xs text-text-error">{fieldStates.teamId.error.message}</span>
              )}
            </div>
          </FieldWithStatusIndicator>
        )}
        {adapter.isVisible('estimationId') && (
          <FieldWithStatusIndicator
            inputId="plan-scheduling-effort-estimate"
            name={t(($) => $.adaptivePlanner.itemPanel.itemSection.effortEstimate)}
            isPending={fieldStates.estimationId.isPending}
            isSuccess={fieldStates.estimationId.isSuccess}
          >
            <div className="flex flex-col gap-1">
              <GroupedEstimationSelect
                key={`plan-estimation-grouped-${estimationId}-${item.id}`}
                id="plan-scheduling-effort-estimate"
                name="planSchedulingEffortEstimate"
                isLoading={isGroupedEstimationLoading}
                value={selectedGroupedEstimationOption}
                onChange={(opt) => handleEstimationChange(opt?.value)}
                groupedOptions={groupedOptions}
                isImputed={adapter.isImputed?.('estimationId')}
                placeholder={t(($) => $.common.select)}
                isDisabled={
                  !adapter.isEditable('estimationId') || adapter.isDisabled('estimationId')
                }
                menuPosition="fixed"
              />
              {fieldStates.estimationId.isError && fieldStates.estimationId.error && (
                <span className="text-xs text-text-error">
                  {fieldStates.estimationId.error.message}
                </span>
              )}
            </div>
          </FieldWithStatusIndicator>
        )}
        {adapter.isVisible('estimationId') && (
          <FieldWithStatusIndicator
            inputId="plan-scheduling-effort-scheme"
            name={t(($) => $.adaptivePlanner.itemPanel.itemSection.effortScheme)}
          >
            <span
              className="text-sm text-text-neutral-secondary"
              data-testid="plan-scheduling-effort-scheme-value"
            >
              {effortSchemeName}
            </span>
          </FieldWithStatusIndicator>
        )}
        {adapter.isVisible('plannedStartDateTime') && (
          <FieldWithStatusIndicator
            inputId="plan-scheduling-planned-start-date"
            name={t(($) => $.adaptivePlanner.itemPanel.itemSection.plannedStartDate)}
            isPending={fieldStates.plannedStartDateTime.isPending}
            isSuccess={fieldStates.plannedStartDateTime.isSuccess}
          >
            {adapter.isEditable('plannedStartDateTime') ? (
              <div className="flex flex-col gap-1">
                <DatePickerField
                  field={{
                    name: 'plannedStartDateTime',
                    value: localDates.plannedStartDateTime,
                    onChange: handlePlannedStartDateChange,
                    onBlur: () => {},
                    ref: () => {},
                  }}
                  placeholder={t(($) => $.common.select)}
                  maxDate={plannedStartDateMaxDate}
                  isClearable
                  clearValue={null}
                  disabled={adapter.isDisabled('plannedStartDateTime')}
                />
                {fieldStates.plannedStartDateTime.isError &&
                  fieldStates.plannedStartDateTime.error && (
                    <span className="text-xs text-text-error">
                      {fieldStates.plannedStartDateTime.error.message}
                    </span>
                  )}
              </div>
            ) : localDates.plannedStartDateTime ? (
              <span className="text-sm text-text-neutral-primary">
                {formatLocalDate(
                  toISODateString(localDates.plannedStartDateTime) ??
                    localDates.plannedStartDateTime,
                )}
              </span>
            ) : (
              <EmptyFieldValue />
            )}
          </FieldWithStatusIndicator>
        )}

        {adapter.isVisible('plannedFinishDateTime') && (
          <FieldWithStatusIndicator
            inputId="plan-scheduling-planned-finish-date"
            name={t(($) => $.adaptivePlanner.itemPanel.itemSection.plannedFinishDate)}
            isPending={fieldStates.plannedFinishDateTime.isPending}
            isSuccess={fieldStates.plannedFinishDateTime.isSuccess}
          >
            {adapter.isEditable('plannedFinishDateTime') ? (
              <div className="flex flex-col gap-1">
                <DatePickerField
                  field={{
                    name: 'plannedFinishDateTime',
                    value: localDates.plannedFinishDateTime,
                    onChange: handlePlannedFinishDateChange,
                    onBlur: () => {},
                    ref: () => {},
                  }}
                  placeholder={t(($) => $.common.select)}
                  minDate={plannedFinishDateMinDate}
                  isClearable
                  clearValue={null}
                  disabled={adapter.isDisabled('plannedFinishDateTime')}
                />
                {fieldStates.plannedFinishDateTime.isError &&
                  fieldStates.plannedFinishDateTime.error && (
                    <span className="text-xs text-text-error">
                      {fieldStates.plannedFinishDateTime.error.message}
                    </span>
                  )}
              </div>
            ) : localDates.plannedFinishDateTime ? (
              <span className="text-sm text-text-neutral-primary">
                {formatLocalDate(
                  toISODateString(localDates.plannedFinishDateTime) ??
                    localDates.plannedFinishDateTime,
                )}
              </span>
            ) : (
              <EmptyFieldValue />
            )}
          </FieldWithStatusIndicator>
        )}

        {(isHealthLoading || healthLevel) && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-text-neutral-primary">
              {t(($) => $.adaptivePlanner.itemPanel.planScheduling.health)}
            </span>
            {isHealthLoading ? (
              <Skeleton className="h-5 w-24" data-testid="item-health-badge-skeleton" />
            ) : (
              healthLevel && (
                <HealthBadgeWithTooltip
                  healthLevel={healthLevel}
                  healthTypes={healthRiskTypes}
                  className="max-w-[60%]"
                />
              )
            )}
          </div>
        )}
        <Card className="rounded-md border">
          <CardContent className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-text-neutral-primary">
                {t(($) => $.adaptivePlanner.itemPanel.planScheduling.itemProgress)}
              </span>
              {isHealthLoading ? (
                <Skeleton className="h-4 w-8" data-testid="item-progress-percentage-skeleton" />
              ) : (
                <span
                  className="text-xs font-medium text-text-neutral-primary"
                  data-testid="item-progress-percentage"
                >
                  {progressPercentage}%
                </span>
              )}
            </div>
            {isHealthLoading ? (
              <Skeleton className="h-2 w-full" data-testid="item-progress-bar-skeleton" />
            ) : (
              <Progress value={progressPercentage} max={100} data-testid="item-progress-bar" />
            )}
          </CardContent>
        </Card>
        {(adapter.isVisible('deadlineDateTime') || isEarliestStartVisible || hasScheduleDates) && (
          <Card className="rounded-md border">
            <CardContent className="flex flex-col gap-2">
              <span className="mb-2 text-sm font-medium text-text-neutral-primary">
                {t(($) => $.adaptivePlanner.itemPanel.planScheduling.schedule)}
              </span>
              {plannerModuleEnabled &&
                (isScheduleLoading ? (
                  <Skeleton className="h-7 w-full" data-testid="schedule-card-skeleton" />
                ) : hasScheduleDates ? (
                  <TooltipDatesSection
                    forecastedStart={forecastedStart}
                    forecastedFinish={forecastedFinish}
                    latestFinish={latestFinish}
                    isAtRisk={isItemAtRisk}
                    showFieldTooltips
                  />
                ) : (
                  <Alert variant="info" data-testid="schedule-card-no-schedule-alert">
                    <AlertTitle>
                      {t(($) => $.adaptivePlanner.itemPanel.planScheduling.noSchedule)}
                    </AlertTitle>
                    <AlertDescription>
                      {t(($) => $.adaptivePlanner.itemPanel.planScheduling.noScheduleDescription)}
                    </AlertDescription>
                  </Alert>
                ))}
              {isEarliestStartVisible && (
                <>
                  <Separator />
                  <FieldWithStatusIndicator
                    inputId="plan-scheduling-earliest-start-date"
                    name={t(($) => $.adaptivePlanner.itemPanel.itemSection.earliestStartDate)}
                    isPending={fieldStates.earliestStartDateTime.isPending}
                    isSuccess={fieldStates.earliestStartDateTime.isSuccess}
                    className="pt-2"
                  >
                    {adapter.isEditable('earliestStartDateTime') ? (
                      <div className="flex flex-col gap-1">
                        <DatePickerField
                          field={{
                            name: 'earliestStartDateTime',
                            value: localDates.earliestStartDateTime,
                            onChange: handleEarliestStartDateChange,
                            onBlur: () => {},
                            ref: () => {},
                          }}
                          placeholder={t(($) => $.common.select)}
                          maxDate={startDateMaxDate}
                          isClearable
                          clearValue={null}
                          disabled={adapter.isDisabled('earliestStartDateTime')}
                        />
                        {fieldStates.earliestStartDateTime.isError &&
                          fieldStates.earliestStartDateTime.error && (
                            <span className="text-xs text-text-error">
                              {fieldStates.earliestStartDateTime.error.message}
                            </span>
                          )}
                      </div>
                    ) : localDates.earliestStartDateTime ? (
                      <span className="text-sm text-text-neutral-primary">
                        {formatLocalDate(localDates.earliestStartDateTime)}
                      </span>
                    ) : (
                      <EmptyFieldValue />
                    )}
                  </FieldWithStatusIndicator>
                </>
              )}
              {adapter.isVisible('deadlineDateTime') && (
                <>
                  {plannerModuleEnabled && !isEarliestStartVisible && <Separator />}
                  <FieldWithStatusIndicator
                    inputId="plan-scheduling-deadline-date"
                    name={t(($) => $.adaptivePlanner.itemPanel.itemSection.deadlineDate)}
                    isPending={fieldStates.deadlineDateTime.isPending}
                    isSuccess={fieldStates.deadlineDateTime.isSuccess}
                    className="pt-2"
                  >
                    {adapter.isEditable('deadlineDateTime') ? (
                      <div className="flex flex-col gap-1">
                        <DatePickerField
                          field={{
                            name: 'deadlineDateTime',
                            value: localDates.deadlineDateTime,
                            onChange: handleDeadlineDateChange,
                            onBlur: () => {},
                            ref: () => {},
                          }}
                          placeholder={t(($) => $.common.select)}
                          minDate={finishDateMinDate}
                          isClearable
                          clearValue={null}
                          disabled={adapter.isDisabled('deadlineDateTime')}
                        />
                        {fieldStates.deadlineDateTime.isError &&
                          fieldStates.deadlineDateTime.error && (
                            <span className="text-xs text-text-error">
                              {fieldStates.deadlineDateTime.error.message}
                            </span>
                          )}
                      </div>
                    ) : localDates.deadlineDateTime ? (
                      <span className="text-sm text-text-neutral-primary">
                        {formatLocalDate(localDates.deadlineDateTime)}
                      </span>
                    ) : (
                      <EmptyFieldValue />
                    )}
                  </FieldWithStatusIndicator>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
